import { fmtDay, fmtDaySv, type DateKey } from '@kff/engine';
import type { Lang } from './customer.js';
import { waitingOn, type Step, type VideoState, type WaitingOn } from './video.js';

/** UX-018 / UX-987: the five-step progress model, plus "Ready to deliver" and Cancelled. */
export const STAGES = ['planning', 'editing', 'internal_review', 'with_customer', 'ready_to_deliver', 'delivered'] as const;
export type Stage = (typeof STAGES)[number] | 'cancelled';
export const STAGE_LABEL: Record<Stage, string> = {
  planning: 'Planning',
  editing: 'Editing',
  internal_review: 'Internal review',
  with_customer: 'With client',
  ready_to_deliver: 'Ready to deliver',
  delivered: 'Delivered',
  cancelled: 'Cancelled'
};
export const STAGE_LABEL_SV: Record<Stage, string> = {
  planning: 'Planering',
  editing: 'Redigering',
  internal_review: 'Intern granskning',
  with_customer: 'Hos kund',
  ready_to_deliver: 'Klar att leverera',
  delivered: 'Levererad',
  cancelled: 'Avbruten'
};
/** The stage's label in the viewer's language. */
export const stageLabel = (stage: Stage, lang: Lang = 'en'): string => (lang === 'sv' ? STAGE_LABEL_SV : STAGE_LABEL)[stage];

export interface StageView {
  stage: Stage;
  label: string;
  /** "Storyboard" or "Final formats" (UX-018) */
  tag: string | null;
  /** detail text, e.g. "Internal review · Awaiting producer approval" */
  detail: string;
  /** 1–5 segments filled (Ready to deliver = 4, outlined) */
  position: number;
}

const DETAIL: Record<Step, string> = {
  planned: 'Not started',
  ready_to_edit: 'Shoot done · ready to edit',
  editing: 'In progress',
  internal_review: 'Awaiting CD review',
  internal_changes: 'Internal changes',
  producer_review: 'Awaiting producer approval',
  with_customer: 'Sent to client',
  customer_changes: 'Client feedback received',
  approved: 'Approved',
  delivered: 'Delivered',
  cancelled: 'Cancelled'
};
const DETAIL_SV: Record<Step, string> = {
  planned: 'Inte påbörjad',
  ready_to_edit: 'Inspelning klar · redo att redigera',
  editing: 'Pågår',
  internal_review: 'Väntar på CD-granskning',
  internal_changes: 'Interna ändringar',
  producer_review: 'Väntar på producentens godkännande',
  with_customer: 'Skickad till kund',
  customer_changes: 'Feedback från kund mottagen',
  approved: 'Godkänd',
  delivered: 'Levererad',
  cancelled: 'Avbruten'
};

/** Texts of the stage view, per language (codes stay the same). */
const STAGE_TEXT: Record<Lang, { storyboard: string; final: string; producerApproval: string; storyboardApproved: string; finalNext: string; draft: string; detail: Record<Step, string> }> = {
  en: {
    storyboard: 'Storyboard',
    final: 'Final formats',
    producerApproval: 'Awaiting producer approval',
    storyboardApproved: 'Storyboard approved · start the draft',
    finalNext: 'Draft approved · export final formats',
    draft: 'Draft',
    detail: DETAIL
  },
  sv: {
    storyboard: 'Storyboard',
    final: 'Slutformat',
    producerApproval: 'Väntar på producentens godkännande',
    storyboardApproved: 'Storyboard godkänd · börja på utkastet',
    finalNext: 'Utkast godkänt · exportera slutformat',
    draft: 'Utkast',
    detail: DETAIL_SV
  }
};

export function stageOf(s: Pick<VideoState, 'phase' | 'step' | 'needsFinalFormats' | 'customerRound'>, lang: Lang = 'en'): StageView {
  const tx = STAGE_TEXT[lang];
  const tag = s.phase === 'storyboard' ? tx.storyboard : s.phase === 'final' ? tx.final : null;
  let stage: Stage;
  switch (s.step) {
    case 'planned':
      stage = 'planning';
      break;
    case 'ready_to_edit':
    case 'editing':
    case 'internal_changes':
    case 'customer_changes':
      stage = 'editing';
      break;
    case 'internal_review':
    case 'producer_review':
      stage = 'internal_review';
      break;
    case 'with_customer':
      stage = 'with_customer';
      break;
    case 'approved':
      stage = s.phase === 'storyboard' || (s.phase === 'draft' && s.needsFinalFormats) ? 'editing' : 'ready_to_deliver';
      break;
    case 'delivered':
      stage = 'delivered';
      break;
    case 'cancelled':
      stage = 'cancelled';
      break;
  }
  const position = stage === 'cancelled' ? 0 : STAGES.indexOf(stage) >= 4 ? (stage === 'delivered' ? 5 : 4) : STAGES.indexOf(stage) + 1;
  let detail = s.phase === 'final' && s.step === 'internal_review' ? tx.producerApproval : tx.detail[s.step];
  if (s.step === 'approved' && stage === 'editing') detail = s.phase === 'storyboard' ? tx.storyboardApproved : tx.finalNext;
  if (s.customerRound > 0 && (s.step === 'with_customer' || s.step === 'customer_changes')) detail += ` · ${tx.draft} ${s.customerRound}`;
  const label = stageLabel(stage, lang);
  return { stage, label, tag, detail: `${label} · ${detail}`, position };
}

/* ---------------- project roll-up (DATA-160, DATA-158) ---------------- */

export const PROJECT_STATUSES = ['planned', 'pre_production', 'in_production', 'in_review', 'delivering', 'delivered', 'closed'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
export type BookingStatus = 'not_started' | 'preliminary' | 'booked' | 'reschedule' | 'paused' | 'cancelled';

export interface RollupVideo extends Pick<VideoState, 'phase' | 'step' | 'needsFinalFormats' | 'customerRound' | 'nextDraftDue'> {
  firstDraftDue: DateKey | null;
  internalDeadline: DateKey | null;
}

export interface Rollup {
  status: ProjectStatus;
  waitingOn: Exclude<WaitingOn, 'none'>[];
  nextDeadline: DateKey | null;
}

/** Steps from internal_review up to and including approved, in DATA-149 order (DATA-160 "in_review"). */
const IN_REVIEW: readonly Step[] = ['internal_review', 'internal_changes', 'producer_review', 'with_customer', 'customer_changes', 'approved'];
const isReadyToDeliver = (v: RollupVideo) => v.step === 'approved' && (v.phase === 'final' || (v.phase === 'draft' && !v.needsFinalFormats));

/** DATA-160 (without shoots for now: `in_production` comes from the videos, `pre_production` from Booked). */
export function rollup(p: { closedAt: string | null; bookingStatus: BookingStatus }, videos: RollupVideo[]): Rollup {
  const live = videos.filter((v) => v.step !== 'cancelled');
  let status: ProjectStatus;
  if (p.closedAt) status = 'closed';
  else if (live.length && live.every((v) => v.step === 'delivered')) status = 'delivered';
  else if (live.length && live.every((v) => v.step === 'delivered' || isReadyToDeliver(v))) status = 'delivering';
  else if (videos.some((v) => IN_REVIEW.includes(v.step))) status = 'in_review';
  else if (videos.some((v) => v.step === 'ready_to_edit' || v.step === 'editing')) status = 'in_production';
  else if (p.bookingStatus === 'booked') status = 'pre_production';
  else status = 'planned';
  const order: Exclude<WaitingOn, 'none'>[] = ['producer', 'cd', 'editor', 'customer'];
  const w = new Set(videos.map((v) => waitingOn(v)).filter((x): x is Exclude<WaitingOn, 'none'> => x !== 'none'));
  return { status, waitingOn: order.filter((x) => w.has(x)), nextDeadline: nextDeadline(videos) };
}

/** The earliest date KFF still owes: internal deadline, first draft (before any round) or next draft (after feedback). */
export function videoDue(v: RollupVideo): DateKey | null {
  switch (v.step) {
    case 'delivered':
    case 'cancelled':
    case 'with_customer':
    case 'approved':
      return null;
    case 'customer_changes':
      return v.nextDraftDue;
    default: {
      const ds = [v.internalDeadline, v.customerRound === 0 ? v.firstDraftDue : null].filter((d): d is DateKey => Boolean(d));
      return ds.sort()[0] ?? null;
    }
  }
}

export function nextDeadline(videos: RollupVideo[]): DateKey | null {
  return (
    videos
      .map(videoDue)
      .filter((d): d is DateKey => Boolean(d))
      .sort()[0] ?? null
  );
}

/** UX-987: "2 of 4 delivered · 1 editing · 1 with client". */
export function distribution(videos: Pick<RollupVideo, 'phase' | 'step' | 'needsFinalFormats' | 'customerRound'>[], lang: Lang = 'en'): string {
  const sv = lang === 'sv';
  const live = videos.filter((v) => v.step !== 'cancelled');
  if (!live.length) return videos.length ? (sv ? 'Alla avbrutna' : 'All cancelled') : sv ? 'Inga videor än' : 'No videos yet';
  const delivered = live.filter((v) => v.step === 'delivered').length;
  const counts = new Map<Stage, number>();
  for (const v of live) {
    const s = stageOf(v).stage;
    if (s !== 'delivered') counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  // nothing delivered yet: lead with the count, "2 videos · 1 editing · 1 internal review"
  const lead = delivered ? (sv ? `${delivered} av ${live.length} levererade` : `${delivered} of ${live.length} delivered`) : sv ? `${live.length} ${live.length === 1 ? 'video' : 'videor'}` : `${live.length} video${live.length === 1 ? '' : 's'}`;
  const parts = [lead];
  for (const s of STAGES) {
    const n = counts.get(s);
    if (n) parts.push(`${n} ${stageLabel(s, lang).toLowerCase()}`);
  }
  return parts.join(' · ');
}

/** UX-018: the project's indicator is the lowest step among its open videos; none = Planning; all delivered = Delivered. */
export function projectStage(videos: Pick<RollupVideo, 'phase' | 'step' | 'needsFinalFormats' | 'customerRound'>[], closed = false, lang: Lang = 'en'): StageView {
  const live = videos.filter((v) => v.step !== 'cancelled');
  if (closed) {
    const t = lang === 'sv' ? 'Stängt' : 'Closed';
    return { stage: 'delivered', label: t, tag: null, detail: t, position: 5 };
  }
  if (!live.length) return { stage: 'planning', label: stageLabel('planning', lang), tag: null, detail: distribution(videos, lang), position: 1 };
  const openOnes = live.filter((v) => v.step !== 'delivered');
  if (!openOnes.length) return { stage: 'delivered', label: stageLabel('delivered', lang), tag: null, detail: distribution(videos, lang), position: 5 };
  const lowest = openOnes.map((v) => stageOf(v, lang)).sort((a, b) => a.position - b.position)[0]!;
  return { ...lowest, tag: null, detail: distribution(videos, lang) };
}

/* ---------------- next step (UX-019, simplified for manual mode) ---------------- */

export interface Viewer {
  personId: string;
  roles: readonly string[];
}
export interface NextStepVideo extends RollupVideo {
  id: string;
  name: string;
  editorId: string | null;
}
export interface Team {
  producerIds: string[];
  cdIds: string[];
  editorIds: string[];
  names: Record<string, string>;
}
export interface NextStep {
  text: string;
  /** ≤ 6 words for list rows */
  phrase: string;
  mine: boolean;
  waitingOn: WaitingOn;
  due: DateKey | null;
  /** The people the step waits on (shown as avatars); empty when it waits on the client or nobody. */
  who?: { id: string; name: string }[];
}

const first = (n: string | undefined) => (n ?? '').split(/\s+/)[0] || '';

/** Who the video waits on, as people: its editor (or the project's), the CD(s), the producer(s). */
function owners(v: NextStepVideo, w: WaitingOn, team: Team): string[] {
  if (w === 'editor') return v.editorId ? [v.editorId] : team.editorIds;
  // no CD on the project (owner 2026-11-01): the producer takes the internal review, so the video never sits silently.
  // Only a stand-in: the CD and the producer stay two roles.
  if (w === 'cd') return team.cdIds.length ? team.cdIds : team.producerIds;
  // DATA-158 / UX-019: a planned video is the producer's
  if (w === 'producer' || (w === 'none' && v.step === 'planned')) return team.producerIds;
  return [];
}

/** The viewer's action per step, and the other next-step texts, per language. */
const NEXT_TEXT = {
  en: {
    your: {
      planned: 'plan the video',
      ready_to_edit: 'start editing',
      editing: 'submit for internal review',
      internal_changes: 'make the internal changes',
      customer_changes: "make the client's changes",
      internal_review: 'review internally',
      producer_review: 'approve and send to the client'
    } as Partial<Record<Step, string>>,
    approveSend: 'approve and send to the client',
    startDraft: 'start the draft',
    submitFinal: 'export final formats',
    deliver: 'deliver',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    withClient: (n: number) => `With the client · Draft ${n}`,
    due: (d: DateKey) => ` · due ${fmtDay(d)}`,
    yourTurn: (a: string) => `Your turn: ${a}`,
    needs: { editor: 'Needs an editor', cd: 'Needs a CD', producer: 'Needs a producer' },
    needsRoles: (list: string) => `Needs ${list}`,
    roleNoun: { editor: 'an editor', cd: 'a CD', producer: 'a producer' },
    and: ' and ',
    waitingFor: (list: string) => `Waiting for ${list}'s OK`,
    withPeople: (list: string) => `With ${list}`,
    addVideosText: 'Your turn: add the videos',
    addVideosPhrase: 'Your turn: add videos',
    noVideos: 'No videos yet',
    allDelivered: (n: number) => `All ${n} delivered`,
    deliverManyText: (n: number) => `Your turn: deliver ${n} videos`,
    deliverManyPhrase: (n: number) => `Your turn: deliver ${n}`,
    more: (n: number) => ` (+${n} more)`,
    theClient: 'the client',
    waitingTeam: 'Waiting on the team',
    waitingOn: (list: string) => `Waiting on ${list}`
  },
  sv: {
    your: {
      planned: 'planera videon',
      ready_to_edit: 'börja redigera',
      editing: 'skicka till intern granskning',
      internal_changes: 'gör de interna ändringarna',
      customer_changes: 'gör kundens ändringar',
      internal_review: 'granska internt',
      producer_review: 'godkänn och skicka till kund'
    } as Partial<Record<Step, string>>,
    approveSend: 'godkänn och skicka till kund',
    startDraft: 'börja på utkastet',
    submitFinal: 'exportera slutformat',
    deliver: 'leverera',
    delivered: 'Levererad',
    cancelled: 'Avbruten',
    withClient: (n: number) => `Hos kund · Utkast ${n}`,
    due: (d: DateKey) => ` · senast ${fmtDaySv(d)}`,
    yourTurn: (a: string) => `Din tur: ${a}`,
    needs: { editor: 'Behöver en redigerare', cd: 'Behöver en CD', producer: 'Behöver en producent' },
    needsRoles: (list: string) => `Behöver ${list}`,
    roleNoun: { editor: 'en redigerare', cd: 'en CD', producer: 'en producent' },
    and: ' och ',
    waitingFor: (list: string) => `Väntar på OK från ${list}`,
    withPeople: (list: string) => `Hos ${list}`,
    addVideosText: 'Din tur: lägg till videorna',
    addVideosPhrase: 'Din tur: lägg till videor',
    noVideos: 'Inga videor än',
    allDelivered: (n: number) => `Alla ${n} levererade`,
    deliverManyText: (n: number) => `Din tur: leverera ${n} videor`,
    deliverManyPhrase: (n: number) => `Din tur: leverera ${n}`,
    more: (n: number) => ` (+${n} till)`,
    theClient: 'kunden',
    waitingTeam: 'Väntar på teamet',
    waitingOn: (list: string) => `Väntar på ${list}`
  }
} as const;

/** An approved video whose next action is Deliver (not "start the draft" or "submit final formats"). */
const deliverNext = (v: Pick<NextStepVideo, 'phase' | 'step' | 'needsFinalFormats'>) => v.step === 'approved' && v.phase !== 'storyboard' && !(v.phase === 'draft' && v.needsFinalFormats);

export function videoNextStep(v: NextStepVideo, team: Team, viewer: Viewer, lang: Lang = 'en'): NextStep {
  const tx = NEXT_TEXT[lang];
  const w = waitingOn(v);
  const due = videoDue(v);
  const who = owners(v, w, team);
  const mine = who.includes(viewer.personId);
  const names = who.map((id) => first(team.names[id])).filter(Boolean);
  const people = who.filter((id) => team.names[id]).map((id) => ({ id, name: team.names[id]! }));
  if (v.step === 'delivered') return { text: tx.delivered, phrase: tx.delivered, mine: false, waitingOn: w, due: null };
  if (v.step === 'cancelled') return { text: tx.cancelled, phrase: tx.cancelled, mine: false, waitingOn: w, due: null };
  if (w === 'customer') {
    const t = tx.withClient(v.customerRound || 1);
    return { text: t, phrase: t, mine: false, waitingOn: w, due: null };
  }
  let action = tx.your[v.step];
  if (v.step === 'internal_review' && v.phase === 'final') action = tx.approveSend;
  if (v.step === 'approved') action = v.phase === 'storyboard' ? tx.startDraft : v.phase === 'draft' && v.needsFinalFormats ? tx.submitFinal : tx.deliver;
  const dueText = due ? tx.due(due) : '';
  if (mine) return { text: `${tx.yourTurn(String(action))}${dueText}`, phrase: tx.yourTurn(String(action)), mine, waitingOn: w, due, who: people };
  if (!names.length) {
    const role = w === 'editor' ? tx.needs.editor : w === 'cd' ? tx.needs.cd : tx.needs.producer;
    return { text: `${role}${dueText}`, phrase: role, mine, waitingOn: w, due };
  }
  const list = names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(tx.and);
  const t = w === 'cd' || (w === 'producer' && v.step !== 'planned') ? tx.waitingFor(list) : tx.withPeople(list);
  return { text: `${t}${dueText}`, phrase: t, mine, waitingOn: w, due, who: people };
}

/** UX-019 project order: producer_review, ready to deliver, internal_review, customer_changes, internal_changes, editing, ready_to_edit, planned; then deadline. */
const PRIORITY: Step[] = ['producer_review', 'approved', 'internal_review', 'customer_changes', 'internal_changes', 'editing', 'ready_to_edit', 'planned'];

export function projectNextStep(videos: NextStepVideo[], team: Team, viewer: Viewer, lang: Lang = 'en'): NextStep {
  const tx = NEXT_TEXT[lang];
  const live = videos.filter((v) => v.step !== 'cancelled');
  if (!live.length) {
    const mine = team.producerIds.includes(viewer.personId);
    return { text: mine ? tx.addVideosText : tx.noVideos, phrase: mine ? tx.addVideosPhrase : tx.noVideos, mine, waitingOn: 'producer', due: null };
  }
  if (live.every((v) => v.step === 'delivered')) {
    const t = tx.allDelivered(live.length);
    return { text: t, phrase: t, mine: false, waitingOn: 'none', due: null };
  }
  const steps = live.map((v) => ({ v, n: videoNextStep(v, team, viewer, lang) }));
  const rank = (s: Step) => (PRIORITY.indexOf(s) < 0 ? 99 : PRIORITY.indexOf(s));
  const mine = steps.filter((x) => x.n.mine).sort((a, b) => rank(a.v.step) - rank(b.v.step) || String(a.n.due ?? '9').localeCompare(String(b.n.due ?? '9')));
  if (mine.length) {
    const top = mine[0]!;
    // the viewer's own "deliver" steps (compared on the codes, so it works in every language)
    const deliverable = mine.filter((x) => deliverNext(x.v)).length;
    if (deliverable >= 2) return { text: tx.deliverManyText(deliverable), phrase: tx.deliverManyPhrase(deliverable), mine: true, waitingOn: 'producer', due: null };
    const more = mine.length > 1 ? tx.more(mine.length - 1) : '';
    return { ...top.n, text: `${top.n.phrase} – ${top.v.name}${more}` };
  }
  const people = new Set<string>();
  const who = new Map<string, string>();
  let client = false;
  // the roles a video waits on that nobody on the project fills
  const missing = new Set<'editor' | 'cd' | 'producer'>();
  for (const x of steps) {
    const w = x.n.waitingOn;
    if (w === 'customer') client = true;
    else {
      const ids = owners(x.v, w, team).filter((id) => team.names[id]);
      if (!ids.length && w !== 'none') missing.add(w);
      for (const id of ids) {
        people.add(first(team.names[id]));
        who.set(id, team.names[id]!);
      }
    }
  }
  const names = [...people];
  if (client) names.push(tx.theClient);
  if (!names.length) {
    const roles = (['editor', 'cd', 'producer'] as const).filter((r) => missing.has(r)).map((r) => tx.roleNoun[r]);
    const t = roles.length ? tx.needsRoles(roles.length === 1 ? roles[0]! : `${roles.slice(0, -1).join(', ')}${tx.and}${roles.at(-1)}`) : tx.waitingTeam;
    return { text: t, phrase: t, mine: false, waitingOn: 'none', due: null };
  }
  const list = names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.length === 1 ? names[0]! : `${names.slice(0, -1).join(', ')}${tx.and}${names.at(-1)}`;
  return { text: tx.waitingOn(list), phrase: tx.waitingOn(list), mine: false, waitingOn: 'none', due: nextDeadline(live), who: [...who].map(([id, name]) => ({ id, name })) };
}
